/**
 * App Mode - determines which AR/XR mode is active
 * Works for both 8th Wall (WebAR) and WebXR
 */
export enum AppMode {
  TWIN = "twin",      // 3D viewer only (no AR)
  GROUND = "ground",  // Ground plane AR placement
  IMAGE = "image",    // Image tracking AR
  VPS = "vps",        // Visual Positioning System (Immersal)
}

/**
 * App State - unified state machine for both WebAR and WebXR apps
 * 
 * Flow:
 * LOADING → READY → XR_STARTING → XR_PLACING → EXPERIENCE
 *                                     ↑           ↓
 *                              (reposition) ←←←←←←
 */
export enum AppState {
  // Initial states
  LOADING = "loading",           // App/assets loading
  READY = "ready",               // Ready to start XR
  
  // Onboarding/splash (optional)
  SPLASH = "splash",             // Splash screen
  ONBOARDING = "onboarding",     // Onboarding flow
  
  // XR states (unified for WebAR and WebXR)
  XR_STARTING = "xr_starting",   // XR session starting
  XR_PLACING = "xr_placing",     // AR placement mode (ground/image tracking)
  EXPERIENCE = "experience",      // Main AR experience active
  XR_STOPPING = "xr_stopping",   // XR session stopping
  
  // Recording states
  RECORDING = "recording",       // Video recording active
  PREVIEW = "preview",           // Recording preview
  
  // UI states
  MODAL = "modal",               // Modal dialog open
  INFO = "info",                 // Info panel open
  
  // Legacy aliases (for backwards compatibility)
  AR_SPLASH = "ar_splash",
  AR_LOADING = "ar_loading",
  AR_ONBOARDING = "ar_onboarding",
  AR_VIEWER = "ar_viewer",
  AR_PLACING = "ar_placement",   // Alias for XR_PLACING
  TWIN_VIEWER = "twin_viewer",
  LOCALIZING = "localizing",
  CALIBRATING = "calibrating",
  CALIBRATED = "calibrated",
  ARTWORK_VIEWING = "artwork_viewing",
  MEDIA_SHARE = "media_share",
}

/**
 * Recording State - for video recording flow
 */
export enum RecordingState {
  NONE = "none",
  RECORDING = "recording",
  PROCESSING = "processing",
  PREVIEW = "preview",
  SHARING = "sharing",
}

/**
 * Tracking Status - unified for both 8th Wall and WebXR
 * Note: Matches the 8th Wall TrackingStatus type from app.8w.ts
 */
export type TrackingStatus = 'NORMAL' | 'LIMITED' | undefined;